import { CommonModule, KeyValue } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { tap, Subject, takeUntil, finalize } from 'rxjs';
import { AppService } from 'src/app/services/app.service';
import { MessageToUiService } from 'src/app/services/message-to-ui.service';
import { SettingsService } from 'src/app/services/settings.service';
import { GlobalSpinnerService } from 'src/app/shared/global-spinner/global-spinner.service';
import { UtilsService } from 'src/app/shared/utils.service';
import { ReportsUtilsService } from '../reports-utils.service';
import { ReportsService } from '../reports.service';
import { IReportParameters, RenderFormat } from '../reports-models';
import { LocalizationService } from 'src/app/shared/localization/localization.service';
import { CustomSelectComponent } from 'src/app/shared/custom-select/custom-select.component';
import { CustomMultiSelectComponent } from 'src/app/shared/custom-multi-select/custom-multi-select.component';

@Component({
  selector: 'app-economic',
  templateUrl: './economic.component.html',
  styleUrls: ['./economic.component.scss'],
  imports: [CommonModule, TranslateModule, CustomSelectComponent, CustomMultiSelectComponent]
})
export class EconomicComponent implements OnInit, OnDestroy {
  selectedPeriod = -1;
  periodOptions: KeyValue<any, string>[];
  userAssignedCustomersCount: number;
  debtorTypeOptions: KeyValue<string, string>[];
  customers$ = this.appService.customersSelectItems$
    .pipe(tap(_ => {
      this.userAssignedCustomersCount = _.length;
      if (this.userAssignedCustomersCount == 1)
        {this.selectedCustomers = [_[0].key]; }
    }));

  selectedCustomers: number[];
  selectedDebtorType = 'A';
  includeReminderCases = false;
  renderedReport: any;
  showResults: boolean;
  languageCode: string;

  private readonly unsubscribe$ = new Subject();
  private reportFolder: string;

  constructor(
    private appService: AppService,
    private reportsService: ReportsService,
    private spinnerService: GlobalSpinnerService,
    private utilsService: UtilsService,
    private _sanitizer: DomSanitizer,
    private translateService: TranslateService,
    private notificationService: MessageToUiService,
    private settingsService: SettingsService,
    private reportUtils: ReportsUtilsService,
    private localization: LocalizationService
  ) {}

  ngOnDestroy(): void {
    this.unsubscribe$.next(null);
    this.unsubscribe$.complete();
  }

  ngOnInit(): void {
    this.debtorTypeOptions = this.reportUtils.getDebtorTypeOptions();
    this.periodOptions = this.reportUtils.getYearlyPeriodOptions();
    this.settingsService.getSettingValueByName('ReportServer_FolderForKreditorWeb')
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(settingValue => this.reportFolder = settingValue);
    this.localization.currentLanguage$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(result => this.languageCode = result.toLowerCase());
  }

  onGenerateReport() {
    if (this.selectedCustomers?.length == 0) {
      this.notificationService.errorKey('Errors.Reports.NoCustomersSelected');
      return false;
    }

    const parameters = this.getReportParameters();
    this.spinnerService.show();

    this.reportsService.renderReport(parameters)
      .pipe(finalize(() => {
        this.spinnerService.hide();
      }))
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (result) => {
          const blobData = this.utilsService.b64toBlob(result.data, 'application/pdf');
          this.renderedReport = this._sanitizer.bypassSecurityTrustResourceUrl(window.URL.createObjectURL(blobData));
          this.showResults = true;
        },
        error: () => this.notificationService.genericError()
      });
  }

  private getReportParameters(): IReportParameters {
    const parameters = {
      templatePath: `${this.reportFolder}Economic`,
      displayName: this.translateService.instant('Reports.Economic.Title'),
      format: RenderFormat.Pdf,
      parameters: {
        CustomerNos: this.selectedCustomers?.join(',').toString(),
        debtorType: this.selectedDebtorType,
        Interval: '0',
        languageCode: this.languageCode
      }
    } as IReportParameters;
    if (this.selectedPeriod == -1) {
      const today = new Date();
      parameters.parameters.Year = today.getFullYear().toString();
      parameters.parameters.Month = (today.getMonth() + 1).toString();
    }
    else {
      parameters.parameters.Year = this.selectedPeriod.toString();
      parameters.parameters.Month = '12';
    }
    return parameters;
  }

}
